import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertCircle, CheckCircle2, Loader2, PlayCircle, Tag } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatNumber } from "@/utils/formatNumber";

interface ReprocessNoTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  photoCount: number;
}

export function ReprocessNoTagsDialog({
  open,
  onOpenChange,
  organizationId,
  photoCount
}: ReprocessNoTagsDialogProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [successful, setSuccessful] = useState(0);
  const [failed, setFailed] = useState(0);
  const [status, setStatus] = useState<string>('pending');
  const [isStopping, setIsStopping] = useState(false);

  // Poll for job status updates via Realtime
  useEffect(() => {
    if (!jobId || !open) return;

    console.log('[ReprocessNoTagsDialog] Setting up realtime subscription for job:', jobId);

    const channel = supabase
      .channel(`reprocessing_jobs:${jobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'reprocessing_jobs',
          filter: `id=eq.${jobId}`
        },
        (payload) => {
          console.log('[ReprocessNoTagsDialog] Realtime update:', payload);
          const job = payload.new;
          
          setStatus(job.status);
          setProcessed(job.processed || 0);
          setSuccessful(job.successful || 0);
          setFailed(job.failed || 0);

          if (job.status === 'complete') {
            setIsProcessing(false);
            toast.success(`Reprocessing complete! ${formatNumber(job.successful || 0)} photos processed successfully.`);
            window.dispatchEvent(new CustomEvent('reprocess-no-tags:complete', {
              detail: { processed: job.processed, successful: job.successful, failed: job.failed }
            }));
            setTimeout(() => onOpenChange(false), 1000);
          } else if (job.status === 'stopped') {
            setIsProcessing(false);
            setIsStopping(false);
            toast.info(`Reprocessing stopped. ${formatNumber(job.processed || 0)} photos processed.`);
            window.dispatchEvent(new CustomEvent('reprocess-no-tags:stopped', {
              detail: { processed: job.processed, successful: job.successful, failed: job.failed }
            }));
            setTimeout(() => onOpenChange(false), 1000);
          } else if (job.status === 'failed') {
            setIsProcessing(false);
            toast.error(job.error_message || 'Reprocessing failed');
          }
        }
      )
      .subscribe();

    return () => {
      console.log('[ReprocessNoTagsDialog] Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [jobId, open, onOpenChange]);

  const handleReprocess = async () => {
    try {
      setIsProcessing(true);
      setProcessed(0);
      setSuccessful(0);
      setFailed(0);
      setStatus('pending');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please sign in to reprocess photos");
        setIsProcessing(false);
        return;
      }

      // Call the queue-based edge function
      const { data, error } = await supabase.functions.invoke('queue-reprocess-no-tags', {
        body: { organizationId }
      });

      if (error) {
        console.error('Error starting reprocess:', error);
        
        // Check if it's a 409 conflict (job already running)
        if (error.message?.includes('already running') || data?.existingJobId) {
          const existingJob = data;
          setJobId(existingJob.existingJobId);
          setTotalPhotos(existingJob.progress?.total || 0);
          setProcessed(existingJob.progress?.processed || 0);
          setSuccessful(existingJob.progress?.successful || 0);
          setFailed(existingJob.progress?.failed || 0);
          setStatus(existingJob.jobStatus || 'running');
          
          toast.info(`A reprocessing job is already running. Showing current progress...`);
          return;
        }
        
        toast.error('Failed to start reprocessing');
        setIsProcessing(false);
        return;
      }

      if (!data.success) {
        // Handle the case where a job is already running
        if (data.existingJobId) {
          setJobId(data.existingJobId);
          setTotalPhotos(data.progress?.total || 0);
          setProcessed(data.progress?.processed || 0);
          setSuccessful(data.progress?.successful || 0);
          setFailed(data.progress?.failed || 0);
          setStatus(data.jobStatus || 'running');
          
          toast.info(`A reprocessing job is already running. Showing current progress...`);
          return;
        }
        
        toast.error(data.error || 'Failed to start reprocessing');
        setIsProcessing(false);
        return;
      }

      console.log('[ReprocessNoTagsDialog] Job created:', data);
      setJobId(data.jobId);
      setTotalPhotos(data.totalPhotos);
      setStatus('running');
      toast.success('Reprocessing started! You can close this dialog and check back later.');

    } catch (error) {
      console.error('Error starting reprocess:', error);
      toast.error('Failed to start reprocessing');
      setIsProcessing(false);
    }
  };

  const handleStop = async () => {
    if (!jobId) return;

    try {
      setIsStopping(true);
      
      const { error } = await supabase.functions.invoke('stop-job', {
        body: { jobId }
      });

      if (error) {
        console.error('Error stopping job:', error);
        toast.error('Failed to stop job');
        setIsStopping(false);
        return;
      }

      toast.info('Stop requested. Finishing current photo...');
    } catch (error) {
      console.error('Error stopping job:', error);
      toast.error('Failed to stop job');
      setIsStopping(false);
    }
  };

  // Clean up on dialog close
  useEffect(() => {
    if (!open) {
      setJobId(null);
      setIsProcessing(false);
      setIsStopping(false);
      setProcessed(0);
      setSuccessful(0);
      setFailed(0);
      setStatus('pending');
    }
  }, [open]);

  const percent = totalPhotos > 0 ? Math.min(100, (processed / totalPhotos) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reprocess Photos Without Tags</DialogTitle>
          <DialogDescription>
            This will regenerate AI descriptions, tags, colors, and embeddings for 
            {photoCount > 0 ? ` ${formatNumber(photoCount)} photo${photoCount !== 1 ? 's' : ''}` : ' photos'} 
            {' '}that don't have any tags. Processing happens in small batches and automatically resumes if interrupted.
          </DialogDescription>
        </DialogHeader>

        {isProcessing && totalPhotos > 0 && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Processing photos...</span>
              </div>
              <div className="font-medium">
                {formatNumber(processed)} of {formatNumber(totalPhotos)}
              </div>
            </div>
            <Progress value={percent} />
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">{formatNumber(successful)} / {formatNumber(processed)}</span> successful
              {failed > 0 && (
                <span className="ml-2 text-destructive">{formatNumber(failed)} failed</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              Status: {status}
            </div>
          </div>
        )}

        {!isProcessing && (
          <div className="py-4">
            <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
              <Tag className="w-5 h-5 text-primary mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">What will be reprocessed?</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• AI-generated descriptions and tags</li>
                  <li>• Code and person detection</li>
                  <li>• Color palette extraction</li>
                  <li>• Image embeddings for search</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-3">
                  Processing runs fully automatically in the background. Each batch triggers 
                  the next until complete - no manual intervention needed.
                </p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {isProcessing ? (
            <>
              <Button
                variant="outline"
                onClick={handleStop}
                disabled={isStopping || status === 'stopped'}
              >
                {isStopping ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Stopping...
                  </>
                ) : (
                  'Stop'
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Close & Run in Background
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
              <Button
                onClick={handleReprocess}
                disabled={isProcessing || isStopping || photoCount === 0}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Start Reprocessing'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
